import { useCallback } from 'react';
import { useCallLogs } from './useCallLogs';
import { useAuth } from '@/context/AuthContext';
import { CreateCallLogRequest, UpdateCallLogRequest, CallLog } from '@/types/callLog';
import { TranscriptMessage } from '@/types/speechRecognition';
import { mapTwilioStatusToCallStatus, generateBasicSummary, generateNextAction } from './twilio/call-log-utils';
import { useCallLogSession } from './twilio/useCallLogSession';
import { apiClient } from '@/lib/apiClient';

export type UseTwilioCallLogsReturn = ReturnType<typeof useTwilioCallLogs>;

export const useTwilioCallLogs = () => {
  const { user } = useAuth();
  const userId = user?.id;
  
  // Only fetch call logs if user is authenticated
  const { saveCallLog, updateCallLog: updateLogEntry } = useCallLogs(userId, { shouldFetch: false });
  
  const {
    initializeSession,
    clearSession,
    setSessionAsLogged,
    appendTranscript,
    getSession,
    hasActiveSession,
    getActiveCallLogId,
  } = useCallLogSession();

  const startCallLogging = useCallback(async (
    phoneNumber: string, 
    callSid: string | null, 
    initialTwilioStatus: string = 'connecting',
    contactId?: number,
    leadId?: number
  ): Promise<number | null> => {
    console.log('🔍 [DEBUG] startCallLogging called with:', {
      phoneNumber,
      callSid,
      initialTwilioStatus,
      callSidType: typeof callSid,
      callSidLength: callSid?.length,
      isCallSidEmpty: !callSid
    });

    // Allow creating call logs without call_sid initially
    const finalCallSid = callSid;

    const mappedInitialStatus = mapTwilioStatusToCallStatus(initialTwilioStatus);
    const initialCallData: CreateCallLogRequest = {
      user_id: userId,
      lead_activity_id: 0, // Set to 0 for direct calls
      lead_contact_id: contactId || 0,  // Use provided contactId or default to 0
      lead_company_id: leadId || 0,  // NEW: Pass the lead ID
      phone_number: phoneNumber,
      call_sid: finalCallSid,
      call_status: mappedInitialStatus,
      call_start_time: new Date().toISOString(),
      call_duration: 0,
      transcript: '', 
      ai_summary: `Initiated call to ${phoneNumber}`,
      next_action: generateNextAction(mappedInitialStatus),
    };

    console.log('📞 Initiating call log for:', phoneNumber, 'CallSid:', finalCallSid, 'User ID:', userId, 'Initial Twilio Status:', initialTwilioStatus, 'Mapped DB Status:', mappedInitialStatus);
    console.log('🔍 [DEBUG] Full initialCallData:', initialCallData);
    
    try {
      const savedLog = await saveCallLog(initialCallData);
      if (savedLog && savedLog.id) {
        initializeSession(new Date(savedLog.call_start_time), phoneNumber, finalCallSid, savedLog.id);
        console.log('✅ Initial call log created. Log ID:', savedLog.id, 'with Call SID:', savedLog.call_sid);
        return savedLog.id;
      } else {
        console.error('❌ Failed to create initial call log or log ID missing. No session initialized.');
        return null;
      }
    } catch (error) {
      console.error('❌ Error creating initial call log:', error, '. No session initialized.');
      return null;
    }
  }, [userId, saveCallLog, initializeSession]);

  // Helper function to format rich transcript into readable string
  const formatRichTranscript = useCallback((transcriptMessages: TranscriptMessage[], phoneNumber: string): string => {
    if (!transcriptMessages || transcriptMessages.length === 0) {
      return '';
    }

    return transcriptMessages
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
      .map(message => {
        const time = message.timestamp.toLocaleTimeString();
        const speaker = message.speaker === 'caller' 
          ? `You (${message.phoneNumber || 'Local'})` 
          : `Receiver (${phoneNumber})`;
        return `[${time}] ${speaker}: ${message.text}`;
      })
      .join('\n');
  }, []);

  const finalizeCallLogInDb = useCallback(async (
    finalTwilioStatus: string,
    summary?: string,
    duration?: number,
    richTranscriptData?: TranscriptMessage[]
  ): Promise<CallLog | null> => {
    const session = getSession();
    
    if (!session) {
      console.warn('❌ No active call session to finalize log for.');
      return null;
    }

    if (!session.logId) {
      console.warn('❌ No logId in current session. Cannot update log. Session:', session);
      clearSession();
      return null;
    }

    if (session.isLogged) {
      console.log('ℹ️ Call log already finalized, skipping duplicate. Log ID:', session.logId);
      return null;
    }

    setSessionAsLogged();

    const endTime = new Date();
    const actualDuration = typeof duration === 'number' ? duration : Math.floor((endTime.getTime() - session.startTime.getTime()) / 1000);
    const mappedFinalStatus = mapTwilioStatusToCallStatus(finalTwilioStatus);

    // Use rich transcript if available, otherwise fall back to session transcript
    const richTranscript = richTranscriptData ? formatRichTranscript(richTranscriptData, session.phoneNumber) : '';
    const finalTranscript = richTranscript || session.transcript;
    
    // Save transcript to backend if available
    if (finalTranscript && session.callSid) {
      try {
        await apiClient.post(`/twilio/transcripts/save/${session.callSid}`, { 
          transcript: finalTranscript 
        });
      } catch (error) {
        console.error('Failed to save transcript to backend:', error);
      }
    }

    const callLogUpdates: UpdateCallLogRequest = {
      call_status: mappedFinalStatus,
      call_end_time: endTime.toISOString(),
      call_duration: actualDuration,
      transcript: finalTranscript,
      ai_summary: summary || generateBasicSummary(mappedFinalStatus, actualDuration, session.phoneNumber),
      next_action: generateNextAction(mappedFinalStatus)
    };

    console.log(`💾 Finalizing call log for Log ID: ${session.logId}, User: ${userId}. Twilio Status: ${finalTwilioStatus}, Mapped DB Status: ${mappedFinalStatus}`);
    console.log('📊 Call log update data:', callLogUpdates);
    
    try {
      const updatedLog = await updateLogEntry(session.logId, callLogUpdates);
      console.log('✅ Call log finalized successfully:', updatedLog);
      clearSession(); 
      return updatedLog;
    } catch (error) {
      console.error(`❌ Failed to finalize call log ${session.logId}:`, error);
      return null;
    }
  }, [userId, updateLogEntry, clearSession, getSession, setSessionAsLogged, formatRichTranscript]);

  const updateCallSid = useCallback(async (callSid: string) => {
    const session = getSession();
    if (!session || !session.logId) {
      console.warn('⚠️ Cannot update call SID: No active session or log ID.');
      return null;
    }

    console.log(`🔄 Updating call log ${session.logId} with Call SID: ${callSid}`);
    try {
      const updatedLog = await updateLogEntry(session.logId, { call_sid: callSid });
      console.log('✅ Call SID updated in call log:', updatedLog);
      return updatedLog;
    } catch (error) {
      console.error(`❌ Failed to update call SID for log ${session.logId}:`, error);
      return null;
    }
  }, [updateLogEntry, getSession]);

  const updateCallProgress = useCallback(async (
    twilioCallStatus: string, 
    updates?: Partial<UpdateCallLogRequest> & { transcript_delta?: string } 
  ) => {
    const session = getSession();
    if (!session || !session.logId) {
      console.warn('⚠️ Cannot update call progress: No active session or log ID.');
      return null;
    }

    const mappedStatus = mapTwilioStatusToCallStatus(twilioCallStatus);
    
    if (updates?.transcript_delta) {
      appendTranscript(updates.transcript_delta);
    }
    
    const { transcript_delta, ...otherUpdatesFromInput } = updates || {};
    
    const progressUpdates: UpdateCallLogRequest = {
      call_status: mappedStatus,
      ...otherUpdatesFromInput,
      ...(updates?.transcript_delta && { transcript: session.transcript }), 
    };
    
    console.log(`🔄 Updating call progress for Log ID: ${session.logId}. Twilio status: ${twilioCallStatus}, Mapped DB Status: ${mappedStatus}`);
    try {
      const updatedLog = await updateLogEntry(session.logId, progressUpdates);
      console.log('📊 Call progress updated:', updatedLog);
      return updatedLog;
    } catch (error) {
      console.error(`❌ Failed to update call progress for log ${session.logId}:`, error);
      return null;
    }
  }, [updateLogEntry, getSession, appendTranscript]);

  return {
    startCallLogging,
    finalizeCallLogInDb,
    updateCallProgress,
    updateCallSid,
    clearSession, 
    hasActiveSession, 
    getActiveCallLogId, 
  };
};
